import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Download,
  Eye,
  Filter,
  MoreVertical,
  Plus,
  RefreshCw,
  ClipboardList,
  X,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, BadgeDot } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { avatarGradient } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from '@/shared/store/page';
import { formatShortDateEs } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import {
  EPISODE_STATUS_LABELS,
  EPISODE_STATUS_VARIANTS,
  EpisodeStatusSchema,
  PATIENT_FLOW_LABELS,
  PATIENT_FLOW_VARIANTS,
  PatientFlowStatusSchema,
  listProcedureNames,
  type EpisodeStatus,
  type PatientFlowStatus,
  type ProcedureType,
  type UserId,
} from '@datazo/contracts';
import { useUsersList } from '@/features/users/api';
import { useProceduresList } from './api';

const DEFAULT_PAGE_SIZE = 20;

const ESTADO_OPTIONS = EpisodeStatusSchema.options;
const ESTADO_PACIENTE_OPTIONS = PatientFlowStatusSchema.options;
const TIPO_OPTIONS = listProcedureNames();

export function ProceduresListPage(): JSX.Element {
  usePageTitle('Procedimientos', 'Episodios clínicos y su estado documental');
  const navigate = useNavigate();
  const [estado, setEstado] = useState<EpisodeStatus | ''>('');
  const [estadoPaciente, setEstadoPaciente] = useState<PatientFlowStatus | ''>('');
  const [tipo, setTipo] = useState<ProcedureType | ''>('');
  const [medicoId, setMedicoId] = useState<UserId | ''>('');
  const [desde, setDesde] = useState<string>('');
  const [hasta, setHasta] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  // Directorio de médicos para el filtro (mismo patrón que crear procedimiento).
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const medicoOptions = useMemo(
    () =>
      (usersQuery.data?.items ?? [])
        .filter((u) => (u.rol === 'medico' || u.rol === 'admin') && u.estado === 'activo')
        .map((u) => ({ value: u.id, label: u.nombre })),
    [usersQuery.data],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useProceduresList({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    estado: estado === '' ? undefined : estado,
    estadoPaciente: estadoPaciente === '' ? undefined : estadoPaciente,
    tipo: tipo === '' ? undefined : tipo,
    medicoId: medicoId === '' ? undefined : medicoId,
    desde: desde === '' ? undefined : desde,
    hasta: hasta === '' ? undefined : hasta,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / DEFAULT_PAGE_SIZE);

  const resetFilters = (): void => {
    setEstado('');
    setEstadoPaciente('');
    setTipo('');
    setMedicoId('');
    setDesde('');
    setHasta('');
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Toolbar / filtros */}
      <div className="flex items-end gap-2.5 flex-wrap">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-estado">Estado</Label>
          <select
            id="filter-estado"
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value as EpisodeStatus | '');
              setPage(1);
            }}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[200px]"
          >
            <option value="">Todos los estados</option>
            {ESTADO_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {EPISODE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-estado-paciente">Estado del paciente</Label>
          <select
            id="filter-estado-paciente"
            value={estadoPaciente}
            onChange={(e) => {
              setEstadoPaciente(e.target.value as PatientFlowStatus | '');
              setPage(1);
            }}
            className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[190px]"
          >
            <option value="">Todos</option>
            {ESTADO_PACIENTE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {PATIENT_FLOW_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-medico">Médico</Label>
          <SearchableSelect
            id="filter-medico"
            value={medicoId}
            onChange={(v) => {
              setMedicoId(v as UserId | '');
              setPage(1);
            }}
            placeholder="Todos los médicos"
            clearable
            className="min-w-[220px]"
            options={medicoOptions}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-tipo">Tipo</Label>
          <SearchableSelect
            id="filter-tipo"
            value={tipo}
            onChange={(v) => {
              setTipo(v as ProcedureType | '');
              setPage(1);
            }}
            placeholder="Todos los tipos"
            clearable
            className="min-w-[260px]"
            options={TIPO_OPTIONS.map((t) => ({ value: t, label: t }))}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-desde">Desde</Label>
          <Input
            id="filter-desde"
            type="date"
            value={desde}
            max={hasta === '' ? undefined : hasta}
            onChange={(e) => {
              setDesde(e.target.value);
              setPage(1);
            }}
            className="min-w-[170px]"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="filter-hasta">Hasta</Label>
          <Input
            id="filter-hasta"
            type="date"
            value={hasta}
            min={desde === '' ? undefined : desde}
            onChange={(e) => {
              setHasta(e.target.value);
              setPage(1);
            }}
            className="min-w-[170px]"
          />
        </div>

        <Button variant="secondary" onClick={resetFilters}>
          <Filter className="w-4 h-4" /> Limpiar
        </Button>

        <div className="flex-1" />

        <Button variant="secondary" disabled>
          <Download className="w-4 h-4" /> Exportar
        </Button>
        <Button onClick={() => navigate('/procedimientos/nuevo')}>
          <Plus className="w-4 h-4" /> Nuevo procedimiento
        </Button>
      </div>

      {/* Filtros activos como chips */}
      {(estado !== '' ||
        estadoPaciente !== '' ||
        tipo !== '' ||
        medicoId !== '' ||
        desde !== '' ||
        hasta !== '') && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-text-muted">Filtros activos:</span>
          {estado !== '' && (
            <FilterChip
              label={`Estado: ${EPISODE_STATUS_LABELS[estado]}`}
              onClear={() => setEstado('')}
            />
          )}
          {estadoPaciente !== '' && (
            <FilterChip
              label={`Paciente: ${PATIENT_FLOW_LABELS[estadoPaciente]}`}
              onClear={() => setEstadoPaciente('')}
            />
          )}
          {medicoId !== '' && (
            <FilterChip
              label={`Médico: ${
                medicoOptions.find((m) => m.value === medicoId)?.label ?? medicoId
              }`}
              onClear={() => setMedicoId('')}
            />
          )}
          {tipo !== '' && <FilterChip label={`Tipo: ${tipo}`} onClear={() => setTipo('')} />}
          {desde !== '' && <FilterChip label={`Desde: ${desde}`} onClear={() => setDesde('')} />}
          {hasta !== '' && <FilterChip label={`Hasta: ${hasta}`} onClear={() => setHasta('')} />}
          <button
            type="button"
            onClick={resetFilters}
            className="text-text-muted hover:text-text underline"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar los procedimientos</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Episodios clínicos</CardTitle>
            <CardDescription>
              {isLoading
                ? 'Cargando episodios…'
                : `${total} ${total === 1 ? 'episodio' : 'episodios'} encontrados`}
              {isFetching && !isLoading ? ' · actualizando…' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        {isLoading ? (
          <ListSkeleton />
        ) : !isError && items.length === 0 ? (
          <EmptyState onNew={() => navigate('/procedimientos/nuevo')} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Episodio</TableHead>
                <TableHead>Paciente</TableHead>
                <TableHead>Procedimiento</TableHead>
                <TableHead>Médico</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Estado paciente</TableHead>
                <TableHead className="w-[48px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="stagger-children">
              {items.map((ep) => {
                const variant = EPISODE_STATUS_VARIANTS[ep.estado];
                const label = EPISODE_STATUS_LABELS[ep.estado];
                return (
                  <TableRow
                    key={ep.id}
                    onClick={() => navigate(`/procedimientos/${ep.id}`)}
                    className="cursor-pointer hover:bg-surface-alt animate-fade-in-up transition-colors"
                  >
                    <TableCell>
                      <div className="font-mono text-xs text-text-soft">{ep.id}</div>
                      <div className="text-text-muted text-xs">
                        {formatShortDateEs(ep.fecha)} · {ep.hora}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div
                          className={cn(
                            'w-9 h-9 rounded-md grid place-items-center font-bold text-xs text-white flex-shrink-0',
                            avatarGradient(ep.pacienteId),
                          )}
                        >
                          {patientInitials(ep.pacienteNombre)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-text truncate">
                            {ep.pacienteNombre}
                          </div>
                          <div className="text-text-muted text-xs font-mono">{ep.pacienteId}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                        <span>{ep.tipo}</span>
                        {ep.tiposAdicionales !== undefined &&
                          ep.tiposAdicionales.length > 0 && (
                            <span
                              title={ep.tiposAdicionales.join(' · ')}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-soft text-primary"
                            >
                              +{ep.tiposAdicionales.length} más
                            </span>
                          )}
                      </div>
                      {ep.entidad !== undefined && ep.entidad !== '' && (
                        <div className="text-text-muted text-xs">{ep.entidad}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-text">{ep.medicoNombre}</div>
                      {ep.sala !== undefined && (
                        <div className="text-text-muted text-xs">{ep.sala}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={variant}>
                        <BadgeDot /> {label}
                      </Badge>
                      {ep.estado === 'anulado' &&
                        ep.motivoCancelacion !== undefined &&
                        ep.motivoCancelacion !== '' && (
                          <div
                            className="text-[11px] text-text-muted mt-1 max-w-[180px] truncate"
                            title={ep.motivoCancelacion}
                          >
                            {ep.motivoCancelacion}
                          </div>
                        )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={PATIENT_FLOW_VARIANTS[ep.estadoPaciente]}>
                        <BadgeDot /> {PATIENT_FLOW_LABELS[ep.estadoPaciente]}
                      </Badge>
                    </TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-default"
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={`Acciones para ${ep.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => navigate(`/procedimientos/${ep.id}`)}>
                            <Eye className="w-4 h-4" /> Ver detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => navigate(`/pacientes/${ep.pacienteId}`)}
                          >
                            <Eye className="w-4 h-4" /> Ver paciente
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!isLoading && !isError && total > 0 && (
          <CardFooter className="justify-between">
            <div className="text-text-muted text-sm">
              Página {page} de {totalPages} · {total} {total === 1 ? 'episodio' : 'episodios'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}

/** Devuelve "MR" a partir de "María Elena Rodríguez Peña". */
function patientInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0).toUpperCase() ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0).toUpperCase() ?? '' : '';
  return `${first}${last}` || '?';
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="p-6 flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-surface-alt animate-pulse" />
      ))}
    </div>
  );
}

interface EmptyStateProps {
  onNew(): void;
}

function EmptyState({ onNew }: EmptyStateProps): JSX.Element {
  return (
    <div className="p-10 text-center flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
        <ClipboardList className="w-6 h-6" />
      </div>
      <div className="text-text font-semibold">Sin procedimientos</div>
      <p className="text-text-muted text-sm max-w-sm">
        Aún no hay episodios que coincidan con los filtros actuales. Crea uno nuevo para
        empezar a registrar la trazabilidad clínica.
      </p>
      <Button onClick={onNew}>
        <Plus className="w-4 h-4" /> Nuevo procedimiento
      </Button>
    </div>
  );
}

/** Chip removible para filtros activos. */
function FilterChip({ label, onClear }: { label: string; onClear: () => void }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-soft text-primary border border-primary/20 text-[11px] font-semibold">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Quitar ${label}`}
        className="rounded-full p-0.5 hover:bg-primary/15"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
